import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { Investors } from '@components/book-trial-class-jp/investors'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)
const TurtleSeo = formatSeoConfig({
  title: 'Learn Turtle Graphics for Kids - Fun Python Coding Module',
  description:
    'Discover Turtle graphics for kids to create art with Python coding. Follow simple steps to design amazing graphics. Start learning today!',
  url: 'https://www.codingal.com/courses/turtle-graphics-for-kids/',
  metaImage: 'https://fastly.codingal.com/images/courses/og/turtle.jpg',
})

const { TURTLE } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['pc'].trialCourseQuery}`

const Turtle = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...TurtleSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[TURTLE].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn Turtle with the Top Coding Course Instructors for Kids and Teens"
        description="Dive into the world of Python Turtle Graphics and unlock your child's potential to create amazing graphics and designs! At Codingal, we use Turtle as a fun and engaging way to teach Python programming to kids and teens. From drawing simple shapes to designing complex patterns, Turtle helps young learners grasp essential coding concepts like loops, functions, and conditionals—all while fostering creativity. With Turtle graphics, students can create everything from geometric patterns to interactive designs, bringing their ideas to life through code."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={TURTLE}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <ClassCurriculum
            courseName={TURTLE}
            title="Our Python Turtle class curriculum"
            description={
              <Trans>
                <p>
                  Our curriculum at Codingal is designed to introduce kids and
                  teens to programming through fun and interactive coding with
                  Turtle graphics. This helps students build a strong foundation
                  in Python while learning essential programming concepts like
                  loops, functions, and conditionals. Students will start by
                  learning the basics of Python programming using Turtle
                  graphics, and then move on to understanding how to move the
                  turtle and draw shapes.
                </p>
                <p>
                  They’ll have fun creating squares, triangles, and other simple
                  shapes, and gradually build intricate geometric patterns using
                  loops and functions. They will also learn to create colorful
                  rainbow spirals, draw stars and polygons, and dive into
                  creating recursive designs and fractals. The curriculum is
                  packed with interactive projects that allow students to use
                  Turtle graphics to create engaging and creative designs,
                  making learning Python both fun and practical.
                </p>
              </Trans>
            }
          />
          <Activities courseName={TURTLE} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                At Codingal, we prioritize quality education. Our instructors
                are not only proficient in Node.js but also skilled in teaching
                young minds. They provide personalized attention, ensuring that
                each student grasps the concepts and enjoys the learning
                process.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={TURTLE}
            title={<Trans>An engaging Turtle Python module for kids</Trans>}
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we provide a fun, engaging environment for kids
                  to learn Python using Turtle graphics. Our curriculum
                  simplifies key coding concepts like loops, functions, and
                  conditionals, helping students build a strong foundation.
                  Through interactive projects and hands-on activities, kids
                  gain confidence while learning to create designs and solve
                  problems. Whether they’re new to coding or looking to sharpen
                  their skills, Codingal makes learning Python easy and
                  exciting!
                </span>
              </Trans>
            }
            courseName={TURTLE}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                Codingal’s courses are accredited by STEM.org, ensuring that our
                curriculum meets the highest standards in STEM education. Upon
                completion, students receive a certificate that recognizes their
                achievement and can be a valuable addition to their academic
                portfolio.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Python developer certification"
            description={
              <>
                Completing the Python Turtle curriculum at Codingal is just the
                beginning. Students will earn a Codingal Python Turtle Developer
                Certificate, validating their coding skills and inspiring them
                to dive deeper into Python programming, graphics creation, and
                problem-solving. This certificate will serve as a valuable
                recognition of their efforts, showcasing their commitment to
                learning and preparing them for future opportunities in coding
                and development.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />
          <CodingForStudents
            title="Our Python Turtle course is appropriate for Elementary school students"
            description={
              <>
                At Codingal, our Python Turtle module is designed for elementary
                school students and beginners who are starting their coding
                journey. Turtle graphics provides a fun, hands-on, and visual
                way to learn the basics of Python programming. Through
                interactive activities, students will create shapes, patterns,
                and animations while developing foundational coding skills like
                loops, functions, and conditionals. With step-by-step guidance
                from our expert instructors, kids will gain confidence in both
                coding and creativity, making this module perfect for beginners.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq courseName={TURTLE} title="Frequently Asked Questions" />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['pc']} />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={TURTLE}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Turtle for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Turtle
